/**
 * The same document as a Word file.
 *
 * The PDF path (`./pdf.ts`) photographs the preview: it rasterizes the DOM
 * `paged.ts` has already laid onto letter sheets, which is what makes the
 * printed copy pixel-for-pixel the one the operator was looking at. Nothing
 * here can work that way. A page image dropped into a `.docx` is a PDF with
 * the wrong extension, and the reason anybody asks for Word is to edit the
 * thing — so this rebuilds the document as real paragraphs, real tables and
 * real text, from `./document-content.ts`, which is the one place either
 * renderer is allowed to decide what the document says.
 *
 * Two consequences worth stating plainly, because they look like bugs:
 *
 * The page breaks are Word's, not ours. `paged.ts` exists because a browser
 * will not tell you where a page ends; Word repaginates continuously and would
 * ignore anything we forced. Keep-with-next on headings and unbroken table
 * rows are the closest honest equivalent, and are what this sets.
 *
 * The brand typefaces do not travel. A company's faces are `data:` URIs the
 * browser loads into a stylesheet (see `brand.ts`); a `.docx` names fonts and
 * expects the reader's machine to have them, and Word will not fetch a URI.
 * The logo, the brand color, the wording and the legal identity all survive,
 * which is the branding a client actually reads — but the PDF remains the
 * typographically exact copy, which is why it is still offered beside this.
 */
import type { ResolvedProposalSegment } from "@dantria/core";
import type { ProseDoc } from "./RichText";
import { fmtAmount, formatPercent } from "./api";
import { type BrandPalette, type CompanyIdentity, documentName } from "./brand";
import {
  DOCUMENT_COPY,
  type DocumentContract,
  type DocumentKind,
  documentFacts,
  joinPartNames,
  scheduleRowsFor,
} from "./document-content";
import type { SectionDraft } from "./pages/ContractDetail";
import { type SegmentDraft, acceptanceCells, investmentRows } from "./pages/contract-parts";

/**
 * Word measures in half-points and in twentieths of a point ("twips"), and
 * every size below is written in the unit the API being called wants. Naming
 * them keeps the arithmetic out of the layout, where a stray factor of two
 * reads as a deliberate choice.
 */
const HALF_POINT = 2;
const PT = 20;
/** Letter, one inch of margin — the same page `pdf.ts` prints on, so the two
 *  downloads at least agree about how wide the column is. */
const PAGE_MARGIN_TWIPS = 1440;
/** A `.docx` names a font and hopes; these are the two faces that are on
 *  essentially every machine that opens one, so the fallback is legible
 *  rather than whatever Word reaches for last. */
const BODY_FONT = "Calibri";
const DISPLAY_FONT = "Calibri Light";

/** docx wants bare hex with no `#`; `brandPalette` hands out CSS colors. */
const hex = (color: string) => color.replace(/^#/, "").toUpperCase();

export interface DocxDocument {
  kind: DocumentKind;
  contract: DocumentContract;
  clientName: string;
  identity: CompanyIdentity;
  palette: BrandPalette;
  sections: readonly SectionDraft[];
  segments: readonly SegmentDraft[];
  resolvedByKey: ReadonlyMap<number, ResolvedProposalSegment | null>;
}

/**
 * Decodes the stored logo into the bytes and the display size Word needs.
 *
 * `readLogoFile` only ever writes PNG or JPEG (it re-encodes through a canvas,
 * so an uploaded SVG has already become pixels), but the type is read off the
 * URI rather than assumed — a column written by some earlier version of this
 * app would otherwise embed a mislabelled image, which Word renders as a red X
 * and no explanation. Anything it can't read yields no logo rather than no
 * document: a proposal without its wordmark still says what it costs.
 */
async function decodeLogo(
  logoUrl: string | null,
): Promise<{ data: Uint8Array; type: "png" | "jpg"; width: number; height: number } | null> {
  if (!logoUrl?.startsWith("data:image/")) return null;
  const mime = logoUrl.slice(5, logoUrl.indexOf(";"));
  const type = mime === "image/png" ? "png" : mime === "image/jpeg" ? "jpg" : null;
  if (!type) return null;

  try {
    const blob = await (await fetch(logoUrl)).blob();
    const bitmap = await createImageBitmap(blob);
    // The preview caps the letterhead at 120px tall and 288px wide, keeping
    // the aspect ratio (`h-30 w-auto max-w-72 object-contain`). Word takes a
    // size in CSS pixels, so the same two bounds give the same wordmark.
    const scale = Math.min(120 / bitmap.height, 288 / bitmap.width, 1);
    const size = {
      width: Math.max(1, Math.round(bitmap.width * scale)),
      height: Math.max(1, Math.round(bitmap.height * scale)),
    };
    bitmap.close();
    return { data: new Uint8Array(await blob.arrayBuffer()), type, ...size };
  } catch {
    return null;
  }
}

export async function downloadProposalDocx(doc: DocxDocument, filename: string) {
  const d = await import("docx");
  const {
    AlignmentType,
    BorderStyle,
    Document,
    ImageRun,
    Packer,
    Paragraph,
    Table,
    TableCell,
    TableRow,
    TextRun,
    VerticalAlign,
    WidthType,
  } = d;

  const { kind, contract, clientName, identity, palette, sections, segments, resolvedByKey } = doc;
  const signatory = documentName(identity);
  const currency = contract.currency;
  const logo = await decodeLogo(identity.logoUrl);

  /* ---------------------------------------------------------------- *
   * Runs and paragraphs
   * ---------------------------------------------------------------- */

  interface RunOpts {
    size?: number | undefined;
    bold?: boolean | undefined;
    italics?: boolean | undefined;
    color?: string | undefined;
    font?: string | undefined;
  }

  const text = (value: string, opts: RunOpts = {}) =>
    new TextRun({
      text: value,
      font: opts.font ?? BODY_FONT,
      size: (opts.size ?? 10) * HALF_POINT,
      bold: opts.bold ?? false,
      italics: opts.italics ?? false,
      color: opts.color ?? "404040",
    });

  /**
   * A section's own title: display face, the tenant's ink, ruled underneath in
   * the tenant's color — the same three things the screen gives it.
   *
   * `keepNext` on every heading in this file is doing the work `paged.ts` does
   * on screen. It is the only lever Word offers, and without it a title can
   * end up alone at the foot of a page with its table overleaf.
   */
  const sectionHeading = (value: string) =>
    new Paragraph({
      keepNext: true,
      spacing: { before: 10 * PT, after: 4 * PT },
      border: {
        bottom: { style: BorderStyle.SINGLE, size: 6, color: hex(palette.base), space: 3 },
      },
      children: [
        text(value, { font: DISPLAY_FONT, size: 13, bold: true, color: hex(palette.ink) }),
      ],
    });

  /** A heading *inside* prose. Deliberately plainer than a section title and
   *  never ruled — `RichText.tsx` styles these as bold display text in near
   *  black, and a brand rule under one would be a line the client's screen
   *  copy does not have. */
  const proseHeading = (children: InstanceType<typeof TextRun>[], level: 2 | 3) =>
    new Paragraph({
      keepNext: true,
      spacing: { before: 8 * PT, after: 3 * PT },
      children:
        children.length > 0
          ? children
          : [text(" ", { font: level === 3 ? BODY_FONT : DISPLAY_FONT })],
    });

  /* ---------------------------------------------------------------- *
   * Prose
   * ---------------------------------------------------------------- */

  /**
   * TipTap's document tree, as Word runs.
   *
   * Walked rather than converted through HTML: the prose is *stored* as this
   * tree (see `RichText.tsx`), and an HTML round-trip would put a third
   * representation between what was typed and what the client reads. Only the
   * nodes `StarterKit` is configured to produce are handled — code blocks and
   * horizontal rules are switched off there, and anything unrecognised is
   * walked through for its text rather than dropped, so a node added later
   * degrades to plain words instead of vanishing from a signed document.
   */
  function proseRuns(node: unknown, marks: RunOpts = {}): InstanceType<typeof TextRun>[] {
    if (!node || typeof node !== "object") return [];
    const n = node as {
      type?: string;
      text?: string;
      marks?: { type: string }[];
      content?: unknown[];
    };
    if (n.type === "text") {
      const own = { ...marks };
      for (const mark of n.marks ?? []) {
        if (mark.type === "bold") own.bold = true;
        if (mark.type === "italic") own.italics = true;
      }
      return n.text ? [text(n.text, own)] : [];
    }
    if (n.type === "hardBreak") return [new TextRun({ break: 1 })];
    return (n.content ?? []).flatMap((child) => proseRuns(child, marks));
  }

  function proseParagraphs(body: ProseDoc | null): InstanceType<typeof Paragraph>[] {
    const blocks = (body?.content ?? []) as {
      type?: string;
      attrs?: { level?: number };
      content?: unknown[];
    }[];
    const out: InstanceType<typeof Paragraph>[] = [];

    const walk = (
      nodes: readonly unknown[],
      listItem: { kind: "bullet" | "number"; index: number } | null,
    ) => {
      // The marker belongs to the item's *first* paragraph only; a list item
      // that wraps to a second one would otherwise be bulleted twice.
      let list = listItem;
      for (const raw of nodes) {
        const node = raw as { type?: string; attrs?: { level?: number }; content?: unknown[] };
        if (node.type === "bulletList" || node.type === "orderedList") {
          let index = 1;
          for (const item of node.content ?? []) {
            const li = item as { content?: unknown[] };
            walk(li.content ?? [], {
              kind: node.type === "bulletList" ? "bullet" : "number",
              index: index++,
            });
          }
          continue;
        }
        if (node.type === "heading") {
          // h1 in a prose block renders at h2's weight on screen; two sizes is
          // all the document has ever had, and inventing a third here would
          // show up as a heading the preview never draws.
          const level = node.attrs?.level === 3 ? 3 : 2;
          out.push(
            proseHeading(
              proseRuns(node, {
                bold: true,
                color: "111827",
                size: level === 3 ? 10 : 11,
                font: level === 3 ? BODY_FONT : DISPLAY_FONT,
              }),
              level,
            ),
          );
          continue;
        }
        if (node.type === "blockquote") {
          // Indented and muted rather than ruled: Word draws a paragraph
          // border across the full column, not down the left edge of the
          // quote, which would read as a rule under the page.
          for (const inner of node.content ?? []) {
            out.push(
              new Paragraph({
                spacing: { after: 4 * PT },
                indent: { left: 18 * PT },
                children: proseRuns(inner, { color: "525252", italics: true }),
              }),
            );
          }
          continue;
        }
        const runs = proseRuns(node);
        if (runs.length === 0 && !list) {
          // An empty paragraph is deliberate whitespace in the document
          // somebody composed; keep it rather than closing the gap up.
          out.push(new Paragraph({ children: [], spacing: { after: 4 * PT } }));
          continue;
        }
        out.push(
          new Paragraph({
            spacing: { after: 4 * PT },
            // Word's own numbering would restart per list in ways that need a
            // numbering definition per document; a literal marker is both
            // simpler and exactly what the reader sees on screen.
            ...(list ? { indent: { left: 24 * PT, hanging: 12 * PT } } : {}),
            children: list
              ? [text(list.kind === "bullet" ? "• " : `${list.index}. `), ...runs]
              : runs,
          }),
        );
        list = null;
      }
    };

    walk(blocks, null);
    return out;
  }

  /* ---------------------------------------------------------------- *
   * Tables
   * ---------------------------------------------------------------- */

  const cell = (
    children: InstanceType<typeof Paragraph>[],
    opts: { fill?: string | undefined; columnSpan?: number | undefined } = {},
  ) =>
    new TableCell({
      children,
      ...(opts.columnSpan ? { columnSpan: opts.columnSpan } : {}),
      ...(opts.fill ? { shading: { fill: opts.fill } } : {}),
      verticalAlign: VerticalAlign.CENTER,
      margins: { top: 3 * PT, bottom: 3 * PT, left: 4 * PT, right: 4 * PT },
    });

  const cellText = (
    value: string,
    opts: {
      bold?: boolean | undefined;
      right?: boolean | undefined;
      color?: string | undefined;
      size?: number | undefined;
      caps?: boolean | undefined;
    } = {},
  ) => [
    new Paragraph({
      alignment: opts.right ? AlignmentType.RIGHT : AlignmentType.LEFT,
      children: [
        text(opts.caps ? value.toUpperCase() : value, {
          bold: opts.bold,
          color: opts.color,
          size: opts.size,
        }),
      ],
    }),
  ];

  /** No outer frame and no verticals — the client's copy rules its rows and
   *  nothing else, and a boxed table in Word would be the one place the two
   *  downloads visibly disagreed. */
  const RULED = {
    top: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
    bottom: { style: BorderStyle.SINGLE, size: 2, color: "E5E5E5" },
    left: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
    right: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
    insideHorizontal: { style: BorderStyle.SINGLE, size: 2, color: "E5E5E5" },
    insideVertical: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
  } as const;

  const NO_BORDERS = {
    top: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
    bottom: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
    left: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
    right: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
    insideHorizontal: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
    insideVertical: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
  } as const;

  const fullWidth = { size: 100, type: WidthType.PERCENTAGE } as const;

  /** Header rows carry the brand fill, and `cantSplit` keeps a row's wrapped
   *  cells together when Word breaks the table across a page. */
  const headerRow = (labels: readonly { text: string; right?: boolean | undefined }[]) =>
    new TableRow({
      tableHeader: true,
      cantSplit: true,
      children: labels.map((l) =>
        cell(
          cellText(l.text, {
            bold: true,
            right: l.right,
            color: hex(palette.onBase),
            size: 8,
            caps: true,
          }),
          {
            fill: hex(palette.base),
          },
        ),
      ),
    });

  function investmentTable() {
    const { rows, total } = investmentRows(segments);
    return new Table({
      width: fullWidth,
      borders: RULED,
      rows: [
        headerRow([{ text: "Item" }, { text: "Description" }, { text: "Amount", right: true }]),
        ...rows.map(
          (row) =>
            new TableRow({
              cantSplit: true,
              children: [
                cell(cellText(row.itemNo, { color: "737373" })),
                cell(cellText(row.description)),
                cell(cellText(fmtAmount(row.amount, currency), { right: true })),
              ],
            }),
        ),
        new TableRow({
          cantSplit: true,
          children: [
            cell([new Paragraph({ children: [] })], { fill: hex(palette.wash), columnSpan: 2 }),
            cell(
              cellText(fmtAmount(total, currency), {
                right: true,
                bold: true,
                color: hex(palette.ink),
              }),
              {
                fill: hex(palette.wash),
              },
            ),
          ],
        }),
      ],
    });
  }

  function scheduleTable(
    rows: readonly { id: string; name: string; amount: string; share: string }[],
  ) {
    return new Table({
      width: fullWidth,
      borders: RULED,
      rows: [
        headerRow([
          { text: "Milestone" },
          { text: "Percentage", right: true },
          { text: "Amount", right: true },
        ]),
        ...rows.map(
          (row) =>
            new TableRow({
              cantSplit: true,
              children: [
                cell(cellText(row.name)),
                cell(cellText(formatPercent(row.share), { right: true, color: "737373" })),
                cell(cellText(fmtAmount(row.amount, currency), { right: true })),
              ],
            }),
        ),
      ],
    });
  }

  /** Two borderless columns of ruled slots — a grid of label-over-rule pairs,
   *  which is a table in Word for the same reason it is one on paper. */
  function twoColumnGrid(
    cells: readonly ({ label: string; value: string } | null)[],
    ruleColor: string,
  ) {
    const rows: InstanceType<typeof TableRow>[] = [];
    for (let i = 0; i < cells.length; i += 2) {
      rows.push(
        new TableRow({
          cantSplit: true,
          children: [cells[i] ?? null, cells[i + 1] ?? null].map((c) =>
            c
              ? new TableCell({
                  margins: { top: 2 * PT, bottom: 6 * PT, right: 12 * PT },
                  borders: {
                    top: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
                    left: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
                    right: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
                    bottom: { style: BorderStyle.SINGLE, size: 2, color: ruleColor },
                  },
                  children: [
                    new Paragraph({
                      spacing: { after: 1 * PT },
                      children: [text(c.label.toUpperCase(), { size: 7, color: hex(palette.ink) })],
                    }),
                    new Paragraph({ children: [text(c.value || " ", { color: "262626" })] }),
                  ],
                })
              : new TableCell({ borders: NO_BORDERS, children: [new Paragraph({ children: [] })] }),
          ),
        }),
      );
    }
    return new Table({ width: fullWidth, borders: NO_BORDERS, rows });
  }

  /* ---------------------------------------------------------------- *
   * The document
   * ---------------------------------------------------------------- */

  const body: (InstanceType<typeof Paragraph> | InstanceType<typeof Table>)[] = [];

  if (logo) {
    body.push(
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 4 * PT },
        children: [
          new ImageRun({
            data: logo.data,
            type: logo.type,
            transformation: { width: logo.width, height: logo.height },
          }),
        ],
      }),
    );
  }

  body.push(
    new Paragraph({
      alignment: AlignmentType.CENTER,
      keepNext: true,
      children: [
        text(DOCUMENT_COPY[kind].heading, { font: DISPLAY_FONT, size: 22, color: "111827" }),
      ],
    }),
  );

  if (contract.tagline) {
    body.push(
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { before: 3 * PT },
        children: [text(contract.tagline, { color: "737373" })],
      }),
    );
  }

  const contactLine = [identity.phone, identity.email].filter(Boolean).join(" · ");
  body.push(
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { before: contactLine ? 3 * PT : 0, after: 12 * PT },
      // The rule under the letterhead, in the tenant's color — a paragraph
      // border rather than a drawn line, so it moves with the text instead of
      // floating where the first page happened to put it.
      border: {
        bottom: { style: BorderStyle.SINGLE, size: 12, color: hex(palette.base), space: 6 },
      },
      children: contactLine ? [text(contactLine, { size: 8, color: "737373" })] : [],
    }),
  );

  body.push(
    twoColumnGrid(
      documentFacts(kind, contract, clientName).map(([label, value]) => ({ label, value })),
      "F5F5F5",
    ),
  );
  body.push(new Paragraph({ children: [], spacing: { after: 10 * PT } }));

  for (const sec of sections) {
    const { picked, combined } = scheduleRowsFor(sec.segmentKeys, segments, resolvedByKey);

    if (sec.title) {
      const suffix =
        sec.kind === "payment_schedule" && picked.length > 0
          ? ` for ${joinPartNames(picked.map((s) => s.name))}`
          : "";
      body.push(sectionHeading(`${sec.title}${suffix}`));
    }

    if (sec.kind === "prose") {
      body.push(...proseParagraphs(sec.body));
    } else if (sec.kind === "investment") {
      body.push(investmentTable());
    } else if (sec.kind === "payment_schedule") {
      if (combined && combined.rows.length > 0) body.push(scheduleTable(combined.rows));
    } else {
      body.push(twoColumnGrid(acceptanceCells(contract, signatory), "D4D4D4"));
    }

    body.push(new Paragraph({ children: [], spacing: { after: 8 * PT } }));
  }

  const footerLine = [signatory, identity.ein && `EIN ${identity.ein}`].filter(Boolean).join(" · ");
  if (identity.legalName || identity.ein) {
    body.push(
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { before: 10 * PT },
        border: {
          top: { style: BorderStyle.SINGLE, size: 4, color: "E5E5E5", space: 6 },
        },
        children: [text(footerLine, { size: 7, color: "737373" })],
      }),
    );
  }

  const file = new Document({
    // Word shows these in the file's properties and in a document library's
    // columns, which is where a client with twenty proposals finds this one.
    title: `${contract.number} — ${contract.title}`,
    creator: signatory || identity.name || "Dantria",
    description: DOCUMENT_COPY[kind].heading,
    styles: { default: { document: { run: { font: BODY_FONT, size: 10 * HALF_POINT } } } },
    sections: [
      {
        properties: {
          page: {
            margin: {
              top: PAGE_MARGIN_TWIPS,
              bottom: PAGE_MARGIN_TWIPS,
              left: PAGE_MARGIN_TWIPS,
              right: PAGE_MARGIN_TWIPS,
            },
          },
        },
        children: body,
      },
    ],
  });

  const blob = await Packer.toBlob(file);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  // Revoking immediately races the click on some browsers; a frame is enough
  // for the download to have taken its own reference to the blob.
  requestAnimationFrame(() => URL.revokeObjectURL(url));
}
